/**
 * newOrderPoller — resilient, self-scheduling worker that polls
 * /api/admin/orders/new every POLL_INTERVAL_MS and plays a chime when
 * a NEW unseen order arrives.
 *
 * Design invariants (mirror autoPrintController):
 *   - Self-scheduling via setTimeout — a single exception cannot kill
 *     future ticks (see runLoop's try/finally).
 *   - Fully independent from the printer loop. A chime failure is
 *     swallowed by posAudio.chime(); a print failure never reaches here.
 *   - HTTP status is recorded every tick, so a 401 / 500 / network drop
 *     is visible in the diagnostics panel instead of silently killing
 *     the service.
 *   - Reacts to visibility/focus/online events via poke() so a device
 *     that just woke up doesn't wait 5s to know about a new order.
 *   - Uses localStorage `pos_seen_orders` (persists across WebView
 *     reloads) to avoid re-chiming on orders the cashier already saw.
 */
import axios from 'axios';
import * as diag from '../pos/posDiagnostics';
import { chime } from '../pos/posAudio';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 5 * 1000;
const SEEN_KEY = 'pos_seen_orders';
const SEEN_MAX = 100;

let stopped = true;
let running = false;
let scheduledTimer = null;
let initialized = false;

const log = (...args) => {
  // eslint-disable-next-line no-console
  console.log('[NEW_ORDER]', ...args);
};

const loadSeen = () => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_e) { return []; }
};

const saveSeen = (ids) => {
  try {
    const capped = Array.from(new Set(ids)).slice(-SEEN_MAX);
    localStorage.setItem(SEEN_KEY, JSON.stringify(capped));
  } catch (_e) { /* localStorage blocked — in-memory dedup still works */ }
};

const authHeaders = () => {
  if (typeof localStorage === 'undefined') return null;
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

/**
 * One poll cycle. Never throws. Records HTTP status either way.
 */
const tickOnce = async () => {
  const headers = authHeaders();
  if (!headers) {
    diag.recordNewOrderPoll({ httpStatus: 'NO_TOKEN', count: 0 });
    return;
  }
  let httpStatus = null;
  let recentOrders = [];
  try {
    const res = await axios.get(`${API_URL}/api/admin/orders/new`, { headers });
    httpStatus = res.status;
    recentOrders = res.data?.recent_orders || [];
    diag.recordNewOrderPoll({ httpStatus, count: recentOrders.length });
    log(`Poll HTTP ${httpStatus}, recent=${recentOrders.length}`);
  } catch (err) {
    httpStatus = err?.response?.status || 'NETWORK_ERROR';
    diag.recordNewOrderPoll({ httpStatus, count: 0, error: err?.message });
    log(`Poll failed HTTP ${httpStatus}: ${err?.message}`);
    return;
  }

  // First run after mount just seeds the "seen" set — no chime for
  // orders that were already on screen when the POS booted.
  const seen = loadSeen();
  const unseen = recentOrders.filter((o) => !seen.includes(o.id));

  if (!initialized) {
    initialized = true;
    if (recentOrders.length > 0) {
      saveSeen([...seen, ...recentOrders.map((o) => o.id)]);
    }
    return;
  }

  if (unseen.length > 0) {
    log(`${unseen.length} new order(s) — chiming`);
    // chime() catches every possible failure internally (req 8: sound
    // must not take down printing). No await needed.
    chime();
    saveSeen([...seen, ...unseen.map((o) => o.id)]);
  }
};

const runLoop = async () => {
  scheduledTimer = null;
  if (stopped) { running = false; return; }
  try {
    diag.setNewOrderPollerRunning(true);
    await tickOnce();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[NEW_ORDER] tickOnce threw (unexpected):', err?.message || err);
  } finally {
    if (!stopped) {
      scheduledTimer = setTimeout(runLoop, POLL_INTERVAL_MS);
    } else {
      running = false;
      diag.setNewOrderPollerRunning(false);
    }
  }
};

export const start = () => {
  if (running) return;
  stopped = false;
  running = true;
  diag.setNewOrderPollerRunning(true);
  runLoop();
};

export const stop = () => {
  stopped = true;
  running = false;
  if (scheduledTimer) { clearTimeout(scheduledTimer); scheduledTimer = null; }
  diag.setNewOrderPollerRunning(false);
};

export const isRunning = () => running;

export const poke = () => {
  if (stopped) return;
  if (scheduledTimer) { clearTimeout(scheduledTimer); scheduledTimer = null; }
  runLoop();
};

export default { start, stop, isRunning, poke };
